import React from 'react';
import { NeedDTO } from '@bb/domain/dist/types';
import { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from 'hooks';
import {
  Contact,
  Text,
  Paragraph,
  Skeleton,
  theme,
  BasicListingTable,
} from 'components';
import { capitalize } from 'utils';
import { StyledMobileCell } from './StyledComponents';
import MyBrokerageContext from './MyBrokerageContext';

const columns: ColumnsType<NeedDTO> = [
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 75,
    render: (value: string) => <Text strong>{capitalize(value)}</Text>,
  },
  {
    title: 'Subject',
    dataIndex: 'subject',
    key: 'subject',
    width: 100,
  },
  {
    title: 'Note',
    dataIndex: 'note',
    key: 'note',
    width: '50vw',
    render: (note: string) => (
      <Paragraph hideMargin ellipsis={{ rows: 3 }}>
        {note}
      </Paragraph>
    ),
  },
  {
    title: 'Agent',
    dataIndex: 'rep',
    key: 'rep',
    width: 200,
    fixed: 'right',
    onHeaderCell: () => ({
      style: {
        borderLeft: `1px solid ${theme.gray3}`,
      },
    }),
    onCell: () => ({
      style: {
        borderLeft: `1px solid ${theme.gray3}`,
      },
    }),
    render: (rep: NeedDTO['rep']) => (
      <span onClick={e => e.stopPropagation()}>
        <Contact contact={rep} />
      </span>
    ),
  },
];

const HavesWants: React.FC = () => {
  const {
    results: { havesWants },
    loading,
    setRedirect,
    heightOffset,
    selectedRowKeys,
    setSelectedRowKeys,
  } = React.useContext(MyBrokerageContext);
  const { md } = useBreakpoint();

  const preparedDataSource = React.useMemo(() => {
    interface PreparedNeed extends NeedDTO {
      key: string;
    }
    return havesWants.map<PreparedNeed>(haveWant => ({
        key: haveWant._id.toString(),
        ...haveWant,
      }));
  }, [havesWants]);

  const preparedColumns = React.useMemo(() => {
    if (!md) {
      if (!loading) {
        return [
          {
            title: '',
            dataIndex: 'type',
            key: 'type',
            render: (type, { subject, note }) => (
              <StyledMobileCell>
                <Text strong>{capitalize(type)}</Text>
                <div>
                  <Text strong>{subject}</Text>
                  <div>
                    <Text type="secondary" ellipsis>
                      {note}
                    </Text>
                  </div>
                </div>
              </StyledMobileCell>
            ),
          },
        ] as ColumnsType<NeedDTO>;
      }
      return [
        {
          title: '',
          dataIndex: 'type',
          key: 'type',
          render: () => (
            <StyledMobileCell>
              <Skeleton title={false} paragraph={{ rows: 1, width: '100%' }} />
              <div>
                <Skeleton
                  title={false}
                  paragraph={{ rows: 2, width: [150, 220] }}
                />
              </div>
            </StyledMobileCell>
          ),
        },
      ] as ColumnsType<NeedDTO>;
    }
    if (!loading) return columns;
    return columns.map(column => {
      const { key } = column;
      if (['type', 'subject'].includes(key.toString())) {
        return {
          ...column,
          render: () => (
            <Skeleton title={false} paragraph={{ rows: 1, width: '100%' }} />
          ),
        };
      }
      if (key === 'note') {
        return {
          ...column,
          render: () => (
            <Skeleton
              title={false}
              paragraph={{ rows: 2, width: ['100%', '90%'] }}
            />
          ),
        };
      }
      if (key === 'rep') {
        return {
          ...column,
          render: () => <Contact loading />,
        };
      }
      return column;
    });
  }, [loading, md]);

  return (
    <BasicListingTable
      handleRowClick={key => setRedirect(`/needs/${key}/view`)}
      dataSource={preparedDataSource}
      columns={preparedColumns}
      loading={loading}
      heightOffset={heightOffset}
      selectedRowKeys={selectedRowKeys}
      setSelectedRowKeys={setSelectedRowKeys}
    />
  );
};

export default HavesWants;
